import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Radio, Mic, Loader2 } from 'lucide-react';
import { liveNow } from '../api/lives';
import { useCategories } from '../api/categories';
import { useToast } from '../hooks/use-toast';
import LiveFormatPicker from './LiveFormatPicker';

/**
 * Phase 55 · "Live Now" pre-flight dialog.
 * Title and category are MANDATORY before going live.
 */
const GoLiveNowDialog = ({ open, onOpenChange, kind = 'video', initialFormat = '16:9' }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('lives');
  const { categories } = useCategories();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [format, setFormat] = useState(initialFormat);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setCategory('');
    setFormat(initialFormat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const start = async () => {
    if (!title.trim()) { toast({ title: 'Il titolo è obbligatorio', variant: 'destructive' }); return; }
    if (!category.trim()) { toast({ title: 'La categoria è obbligatoria', variant: 'destructive' }); return; }
    setStarting(true);
    try {
      const l = await liveNow({
        kind,
        title: title.trim(),
        category: category.trim(),
        video_format: kind === 'video' ? format : '16:9',
      });
      onOpenChange(false);
      toast({ title: kind === 'audio' ? t('dashboard.audio_live_started_title') : t('dashboard.you_are_live_title') });
      navigate(`/lives/${l.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      toast({
        title: 'Impossibile avviare la Live',
        description: String(detail).split('|').pop() || '',
        variant: 'destructive',
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="go-live-now-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {kind === 'audio' ? <Mic className="h-5 w-5 text-red-500" /> : <Radio className="h-5 w-5 text-red-500" />}
            {kind === 'audio' ? 'Live Audio Now' : 'Live Video Now'}
          </DialogTitle>
          <DialogDescription>
            Titolo e categoria sono obbligatori per andare in diretta.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">Titolo *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder="Es. Q&A del pomeriggio" data-testid="live-now-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">Categoria *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="live-now-category"
            >
              <option value="" disabled>Seleziona una categoria</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          {kind === 'video' && (
            <LiveFormatPicker value={format} onChange={setFormat} testIdPrefix="live-now-dialog-format" compact />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={start} disabled={starting} className="bg-red-500 text-white hover:bg-red-600" data-testid="live-now-start-btn">
            {starting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Radio className="mr-1.5 h-4 w-4" />}
            Vai in diretta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoLiveNowDialog;
